import React, { useEffect, useState } from "react";
import { Plus, Award, Clock, Eye } from "react-bootstrap-icons";
import { Edit2, Trash2 } from "lucide-react";
import { Outlet, useNavigate } from "react-router-dom";
import ModalWrapper from "../../../../components/learning-development/common/ModalWrapper";

import DynamicTable from "../../../../components/learning-development/common/DynamicTable";
import api from "../../../../Axios";
import { toast } from "react-toastify";
import { cleanHtml, defaultDateFormatter, stripHtml } from "../../../../Helper/functionHelper";
import CommonFilter from "../../../../components/common/CommonFilter";
import useLoader from "../../../../hooks/useLoader";
import useModal from "../../../../hooks/useModal";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Badge, IconButton, Tooltip } from "@mui/material";

const LearningProgramList = () => {

  const { showModal } = useModal();
  const navigate = useNavigate();

  const { hideLoader, showLoader } = useLoader();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);

  const [viewLoading, setViewLoading] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [modalType, setModalType] = useState(null); // delete
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    category: "",
    start_date: null,
    end_date: null
  });
  const [filterConfig, setFilterConfig] = useState([
    {
      name: "search",
      type: "text",
      label: "Program name/code",
      grid: 3,
    },
    {
      name: "category",
      type: "select",
      label: "Category",
      grid: 3,
      options: []
    },
    {
      name: "start_date",
      type: "date",
      label: "From Date",
      grid: 3,
    },
    {
      name: "end_date",
      type: "date",
      label: "To Date",
      grid: 3,
    },
    {
      name: "status",
      type: "select",
      label: "Status",
      grid: 3,
      options: [
        { label: "Draft", value: "draft" },
        { label: "Published", value: "published" },
      ]
    },
  ]);

  const fetchPrograms = async (page = currentPage, customFilters = filters) => {
    try {
      setLoading(true);
      showLoader();

      const res = await api.post("/lnd/get-program-list", {
        page,
        limit: itemsPerPage,
        searchData: {
          ...customFilters,
          search: customFilters.search?.trim() || ""
        }
      });

      const responseData = res.data?.data;
      const pagination = responseData?.pagination;

      setPrograms(responseData?.data || []);
      setTotalPages(pagination?.totalPages || 1);
      setTotalItems(pagination?.total || 0);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch programs");
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  useEffect(() => {
    fetchPrograms(currentPage, filters);
  }, [currentPage]);

  const fetchCategories = async () => {
    try {
      const res = await api.get("/lnd/get-category-dropdown");
      const categoryList = res.data.data.data || [];

      const categoryOptions = categoryList.map(cat => ({
        label: cat.title,
        value: cat.id
      }));

      setFilterConfig(prev =>
        prev.map(field =>
          field.name === "category"
            ? { ...field, options: categoryOptions }
            : field
        )
      );

    } catch (err) {
      toast.error("Failed to load categories");
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleExport = async (filters) => {
    const res = await api.post(
      "/lnd/export-program-list",
      { searchData: filters },
      { responseType: "blob" }
    );

    const blob = new Blob([res.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "program_list_export.xlsx";
    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const openViewModal = async (id) => {
    let title = "View Program Details"
    let body;
    let program_detail = {};

    try {
      setViewLoading(true);

      const res = await api.get(`/lnd/${id}/preview`);
      program_detail = res.data?.data?.program;

    } catch (err) {
      toast.error("Failed to load program");
    } finally {
      setViewLoading(false);
    }

    body = (
      <>
        {viewLoading ? (
          <p className="text-center py-4">Loading...</p>
        ) : !program_detail ? (
          <p className="text-center py-4 text-muted">No data found</p>
        ) : (
          <div style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: "5px" }}>

            {/* BASIC INFO CARD */}
            <div className="card shadow-sm mb-3 border-0">
              <div className="card-body">
                <h6 className="fw-bold mb-3 text-primary">Basic Information</h6>
                <div className="row">
                  <div className="col-md-6 mb-2">
                    <small className="text-muted">Name</small>
                    <div>{program_detail.program_name}</div>
                  </div>
                  <div className="col-md-6 mb-2">
                    <small className="text-muted">Code</small>
                    <div>{program_detail.program_code}</div>
                  </div>
                  <div className="col-md-6 mb-2">
                    <small className="text-muted">Mode</small>
                    <div>{program_detail.learning_mode}</div>
                  </div>
                  <div className="col-12">
                    <small className="text-muted">Description</small>
                    <div>{stripHtml(program_detail.description)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* SCHEDULE CARD */}
            <div className="card shadow-sm mb-3 border-0">
              <div className="card-body">
                <h6 className="fw-bold mb-3 text-primary">Schedule</h6>
                <div className="row">
                  <div className="col-md-4 mb-2">
                    <small className="text-muted">Duration</small>
                    <div>{program_detail.duration_hours} hrs</div>
                  </div>
                  <div className="col-md-4 mb-2">
                    <small className="text-muted">Cost</small>
                    <div>₹{program_detail.estimated_cost}</div>
                  </div>
                  <div className="col-md-4 mb-2">
                    <small className="text-muted">Certification</small>
                    <div>{program_detail.is_certified ? "Yes" : "No"}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* MODULES CARD */}
            <div className="card shadow-sm mb-3 border-0">
              <div className="card-body">
                <h6 className="fw-bold mb-3 text-primary">Modules</h6>

                {program_detail.modules?.length ? (
                  program_detail.modules.map((m, i) => (
                    <div
                      key={i}
                      className="p-3 mb-2 rounded"
                      style={{ background: "#f8f9fa" }}
                    >
                      <strong>{m.sequence_order}. {m.title}</strong>
                      <div className="text-muted small mt-1">
                        {/* {stripHtml(m.description)} */}
                        <div dangerouslySetInnerHTML={{ __html: cleanHtml(m.description) }} />
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-muted">No modules available</p>
                )}
              </div>
            </div>

            {/* SKILLS CARD */}
            <div className="card shadow-sm mb-3 border-0">
              <div className="card-body">
                <h6 className="fw-bold mb-3 text-primary">Skill Outcomes</h6>

                {program_detail.skill_outcomes?.length ? (
                  program_detail.skill_outcomes.map((s, i) => (
                    <div
                      key={i}
                      className="p-3 mb-2 rounded"
                      style={{ background: "#f8f9fa" }}
                    >
                      <div><strong>Skill:</strong> {s?.skill?._Stitle || "N/A"}</div>
                      <div><strong>Skill Level:</strong> {s.target_level}</div>
                      <div><strong>Proficiency Level:</strong> {s.rating_gain}</div>
                    </div>
                  ))
                ) : (
                  <p className="text-muted">No skills available</p>
                )}
              </div>
            </div>

          </div>
        )}
      </>
    );

    showModal(title, body, "md");
  };

  const openDeleteModal = (row) => {
    setSelectedProgram(row);
    setModalType("delete");
  };

  const closeModal = () => {
    setModalType(null);
    setSelectedProgram(null);
  };

  const handleDeleteProgram = async () => {
    try {
      await api.delete(`/lnd/delete-program/${selectedProgram.id}`);
      toast.success("Program deleted");
      closeModal();
      fetchPrograms(currentPage, filters);
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const handleEdit = (id) => {
    navigate(`/learning-and-development/learning-program/edit/${id}`);
  };

  const handleRequestOpen = async (key, title, program_id) => {

    const res = await api.get(`/lnd/get-request-count-by-program-id?program_id=${program_id}`);

    let body;

    if (key === "request_employee") {
      body = (
        <div style={{ maxHeight: 300, overflowY: "auto" }} className="p-1">
          {res.data.data.data.length === 0 ? (
            <p className="text-muted text-center mb-0">No request found for this program.</p>
          ) : (
            <div className="d-flex flex-wrap gap-2">
              {res.data.data.data.map((emp) => (
                <span
                  key={emp._UserId}
                  className="badge bg-success text-white px-2 py-1"
                  style={{ borderRadius: 20, fontSize: 10 }}
                >
                  {emp.user._FirstName} {emp.user._LastName}({emp.user._UserEmployeeID})
                </span>
              ))}
            </div>
          )}
        </div>
      );
    }

    showModal(title, body);
  };

  const handleToggleStatus = async (row) => {
    try {
      const newStatus = row.is_active ? 0 : 1;

      await api.put(`/lnd/${row.id}/update-program`, {
        is_active: newStatus
      });

      toast.success("Status updated");

      setPrograms(prev =>
        prev.map(p =>
          p.id === row.id ? { ...p, is_active: newStatus } : p
        )
      );

    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const columns = [
    {
      label: "Program Code",
      field: "program_code",
      className: "fw-semibold",
    },
    {
      label: "Program Name",
      field: "program_name",
      className: "fw-semibold",
    },
    {
      label: "Category Name",
      render: (row) => row.category.title || "N/A",
    },
    {
      label: "Duration",
      render: (row) => (
        <span className="d-flex align-items-center gap-1">
          <Clock size={14} /> {row.duration_hours || 0}h
        </span>
      ),
    },
    {
      label: "View Request",
      align: "center",
      render: (row) => (
        <Tooltip title="View Details">
          <IconButton
            color="success"
            size="small"
            onClick={() => {
              handleRequestOpen("request_employee", "Request Employee Detail", row.id)
            }}
          >
            <Badge
              badgeContent={row.total_requests || "0"}
              color="primary"
              overlap="circular"
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '8px',
                  height: '14px',
                  minWidth: '14px',
                  padding: '0 4px',
                },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>
      ),
    },
    {
      label: "Certificate",
      render: (row) =>
        row.is_certified ? (
          <span className="">
            <Award size={14} /> Yes
          </span>
        ) : (
          <span className="">
            No
          </span>
        ),
    },
    {
      label: "Status",
      render: (row) => {
        let badgeClass = "text-secondary";

        if (row.status === "published") {
          badgeClass = "text-success";
        } else if (row.status === "archived") {
          badgeClass = "text-danger";
        } else if (row.status === "draft") {
          badgeClass = "text-warning";
        }

        return <span className={badgeClass}>{row.status === "published" ? "Published" : row.status === "archived" ? "Archived" : "Draft"}</span>;
      }
    },
    {
      label: "Created At",
      render: (row) => defaultDateFormatter(row.createdAt)
    },
    {
      label: "Active",
      render: (row) => (
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            checked={row.is_active}
            onChange={() => handleToggleStatus(row)}
          />
        </div>
      )
    },
    {
      label: "Actions",
      render: (row) => (
        <div className="d-flex gap-2">

          <div
            className="cursor-pointer"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="View Program Details"
            onClick={() => openViewModal(row.id)}
          >
            <Eye size={16} />
          </div>

          {row.status === "draft" && (<div
            className="cursor-pointer"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="Edit Program"
            onClick={() => handleEdit(row.id)}
          >
            <Edit2 size={16} />
          </div>)}

          {row.status !== "published" && (
            <div
              className="cursor-pointer"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Delete Program"
              onClick={() => openDeleteModal(row)}
            >
              <Trash2 size={16} />
            </div>)}
        </div>
      ),
    },
  ];

  return (
    <div className="container-fluid py-4">

      {/* HEADER */}

      <div className="row g-2 align-items-end mb-4">

        {/* FILTER SECTION */}
        <div className="col-12 col-lg">
          <CommonFilter
            config={filterConfig}
            defaultValues={{
              search: "",
              start_date: null,
              end_date: null
            }}
            onSubmit={(data) => {
              setFilters(data);
              setCurrentPage(1);
              fetchPrograms(1, data);
            }}
            onExport={handleExport}
          />
        </div>

        {/* CREATE BUTTON */}
        <div className="col-12 col-lg-auto d-flex justify-content-end">
          <button
            onClick={() => navigate("create")}
            className="btn btn-success d-flex align-items-center justify-content-center"
            style={{ width: "40px", height: "40px" }}
          >
            <Plus size={24} />
          </button>
        </div>

      </div>


      <Outlet />

      {/* TABLE */}
      <DynamicTable
        columns={columns}
        data={programs}
        rowKey="id"
        loading={loading}
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        onPageChange={(page) => setCurrentPage(page)}
      />

      {modalType === "delete" && (
        <ModalWrapper onClose={closeModal}>
          <h5 className="fw-bold mb-3">Delete Program</h5>

          <p>
            Are you sure you want to delete{" "}
            <strong>{selectedProgram?.program_name}</strong>?
          </p>

          <div className="d-flex gap-2">
            <button
              className="btn btn-danger flex-fill"
              onClick={handleDeleteProgram}
            >
              Delete
            </button>

            <button
              className="btn btn-outline-secondary flex-fill"
              onClick={closeModal}
            >
              Cancel
            </button>
          </div>
        </ModalWrapper>
      )}

    </div>

  );
};

export default LearningProgramList;